import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { useInternals } from "../../context/internalContext";
import { useUnits } from "../../context/unitsContext";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/header";
import Titulo from "../../components/Titulo/titulo";
import FormattedInput from "../../components/FormatdInput/formatFunctions";
import ReactPaginate from "react-paginate";
import { useUsers } from "../../context/usersContext";
import "../../components/InternalsFilterPanel/internalsFilterPanel.css";

const EMPTY_QUANTITATIVE_FILTERS = { name: "", cpf: "", pavilion: "", cell: "" };

const getCellPavilionId = (cell) => Number(
  cell?.pavilion?.id ?? cell?.pavilion_id ?? cell?.pavilion
);

function Quantitative() {
  const { state } = useLocation();
  const { listQuantitative } = useInternals();
  const { listPavillions, listCells } = useUnits();
  const { buildUnitReadParams, effectiveUnitId } = useUsers();
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const itemsPerPage = 10;

  const type = state?.type || "regime";
  const filter = state?.filter || "Fechado";
  const label = state?.label || filter;

  const [draftFilters, setDraftFilters] = useState(EMPTY_QUANTITATIVE_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_QUANTITATIVE_FILTERS);
  const [pavilions, setPavilions] = useState([]);
  const [cells, setCells] = useState([]);

  // Sempre que o card de origem muda (novo type/filter vindo da Home), os filtros extras são reiniciados.
  useEffect(() => {
    setDraftFilters(EMPTY_QUANTITATIVE_FILTERS);
    setAppliedFilters(EMPTY_QUANTITATIVE_FILTERS);
    setCurrentPage(0);
  }, [type, filter]);

  // Pavilhões da unidade ativa.
  useEffect(() => {
    let isMounted = true;
    const fetchPavilions = async () => {
      if (!effectiveUnitId) {
        if (isMounted) setPavilions([]);
        return;
      }
      const response = await listPavillions({ unit: effectiveUnitId });
      const allPavilions = Array.isArray(response?.results) ? response.results : [];
      const filteredPavilions = allPavilions.filter(
        (pavilion) => String(pavilion?.unit) === String(effectiveUnitId)
      );
      if (isMounted) setPavilions(filteredPavilions);
    };
    fetchPavilions();
    return () => { isMounted = false; };
  }, [effectiveUnitId, listPavillions]);

  // Celas do pavilhão selecionado no rascunho de filtros.
  useEffect(() => {
    let isMounted = true;
    const fetchCells = async () => {
      if (!draftFilters.pavilion) {
        if (isMounted) setCells([]);
        return;
      }
      const pavilionId = Number(draftFilters.pavilion);
      const response = await listCells({ pavilion: pavilionId });
      const allCells = Array.isArray(response?.results) ? response.results : [];
      const filteredCells = allCells.filter((cell) => getCellPavilionId(cell) === pavilionId);
      if (isMounted) setCells(filteredCells);
    };
    fetchCells();
    return () => { isMounted = false; };
  }, [draftFilters.pavilion, listCells]);

  useEffect(() => {
    async function fetchData() {
      setIsLoadingData(true);
      try {
        const params = buildUnitReadParams({
          type,
          [type]: filter,
          ...(appliedFilters.name ? { name: appliedFilters.name } : {}),
          ...(appliedFilters.cpf ? { cpf: appliedFilters.cpf.replace(/\D/g, '') } : {}),
          // A cela já implica o pavilhão no backend: se uma cela foi filtrada, o pavilhão não é enviado junto.
          ...(appliedFilters.cell
            ? { cell: appliedFilters.cell }
            : (appliedFilters.pavilion ? { pavilion: appliedFilters.pavilion } : {})),
        });
        const response = await listQuantitative(params);
        if (response?.data) setData(response.data);
      } finally {
        setIsLoadingData(false);
      }
    }
    fetchData();
  }, [type, filter, appliedFilters, listQuantitative, buildUnitReadParams, effectiveUnitId]);

  const handleFilterFieldChange = (field) => (event) => {
    const { value } = event.target;
    setDraftFilters((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'pavilion' ? { cell: '' } : {}),
    }));
  };

  const handleFilterSubmit = (event) => {
    event.preventDefault();
    setAppliedFilters(draftFilters);
    setCurrentPage(0);
  };

  const handleFilterClear = () => {
    setDraftFilters(EMPTY_QUANTITATIVE_FILTERS);
    setAppliedFilters(EMPTY_QUANTITATIVE_FILTERS);
    setCells([]);
    setCurrentPage(0);
  };

  // Paginação
  const pageCount = Math.ceil((data?.length || 0) / itemsPerPage);
  const handlePageClick = ({ selected }) => setCurrentPage(selected);
  const paginatedData = data.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

  const navigate = useNavigate();

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="container">
        <Titulo children={<h2>Internos - {label}</h2>} />

        <form className="internals-filter-card" onSubmit={handleFilterSubmit}>
          <div className="internals-filter-grid">
            <div className="internals-filter-field">
              <label htmlFor="qtdFilterName">Nome</label>
              <FormattedInput
                type="nome"
                id="qtdFilterName"
                name="name"
                placeholder="Nome do interno"
                value={draftFilters.name}
                onChange={handleFilterFieldChange('name')}
              />
            </div>

            <div className="internals-filter-field">
              <label htmlFor="qtdFilterCpf">CPF</label>
              <FormattedInput
                type="cpf"
                id="qtdFilterCpf"
                name="cpf"
                placeholder="000.000.000-00"
                value={draftFilters.cpf}
                onChange={handleFilterFieldChange('cpf')}
              />
            </div>

            <div className="internals-filter-field">
              <label htmlFor="qtdFilterPavilion">Pavilhão</label>
              <select
                id="qtdFilterPavilion"
                value={draftFilters.pavilion}
                onChange={handleFilterFieldChange('pavilion')}
              >
                <option value="">Todos os pavilhões</option>
                {pavilions.map((pavilion) => (
                  <option key={pavilion.id} value={pavilion.id}>
                    {pavilion.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="internals-filter-field">
              <label htmlFor="qtdFilterCell">Cela</label>
              <select
                id="qtdFilterCell"
                value={draftFilters.cell}
                onChange={handleFilterFieldChange('cell')}
                disabled={!draftFilters.pavilion}
              >
                <option value="">
                  {draftFilters.pavilion ? 'Selecione a cela' : 'Selecione o pavilhão primeiro'}
                </option>
                {cells.map((cell) => (
                  <option key={cell.id} value={cell.id}>
                    {cell.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="internals-filter-actions">
            <button type="button" className="btn-edit" onClick={handleFilterClear}>
              Limpar filtros
            </button>
            <button type="submit" className="save-btn">
              Buscar
            </button>
          </div>
        </form>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 8px' }}>
          <span style={{ fontSize: '13px', color: 'var(--texto-destaque)', fontWeight: 600 }}>
            {`Filtrando por: ${label}`}
          </span>
          {!isLoadingData && (
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#b91c1c', background: '#fee2e2', padding: '3px 12px', borderRadius: '12px' }}>
              {data.length} {data.length === 1 ? 'Interno' : 'Internos'}
            </span>
          )}
        </div>

        <div className="content content-table">
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Unidade</th>
                <th>Pavilhão</th>
                <th>Cela</th>
                <th>
                  {type === "status"
                    ? "Status"
                    : type === "regime"
                      ? "Regime"
                      : "Natureza da Prisão"}
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length > 0 ? (
                paginatedData.map((item) => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td>{item.unit}</td>
                    <td>{item.pavilion}</td>
                    <td>{item.cell}</td>
                    <td>
                      {item.status || item.regime || item.nature_of_prision}
                    </td>
                    <td>
                      <button
                        className="btn-agendamento"
                        onClick={() => navigate(`/internos/${item.id}`)}
                        title="Visualizar interno"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6">Nenhum dado encontrado</td>
                </tr>
              )}
            </tbody>
          </table>

          {pageCount > 1 && (
            <ReactPaginate
              previousLabel={"<"}
              nextLabel={">"}
              breakLabel={"..."}
              pageCount={pageCount}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              onPageChange={handlePageClick}
              containerClassName={"pagination"}
              activeClassName={"activeNumber"}
              previousClassName={"prev"}
              nextClassName={"next"}
              pageClassName={"page"}
              breakClassName={"break-me"}
              forcePage={currentPage}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default Quantitative;
